import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import { defineMessages, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { AnyPrice, castPeriod } from "@ct-react/core";
import { useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { buildArticleUrl } from "@shared/urls";
import { TaxableAgeCategoryInput } from "../../../models/cart";
import { AgeCategory } from "../../../models/options";
import { Asset } from "../../../models/medias";
import { useDebounce } from "../../../hooks/debounce";
import { InterhomePayment, InterhomePaymentMode, interhomeUrlParams, useInterhomeContext } from "./interhome-outlet";
import { bookingTranslations } from "../../../i18n/sharable-defs";
import InfoBox from "../../../components/common/info-box";
import ParticipantsInput from "../../../components/common/participants-input";
import CartItem from "../../../components/cart/cart-item";
import { CartPrice } from "../../../components/cart/cart-price";
import { InterhomeErrorDisplay, InterhomeQueryErrorType } from "./error";
import "./interhome.scss";

const RESUME_GQL_DATA = gql`
  query InterhomeBookingResume($articleId: ID!) {
    article: articleDetail(articleId: $articleId) {
      ... on RentalAccommodationArticle {
        id
        slug
        merchant { slug }
        title { value }
        address { resume }
        images @limit(max: 1) { assets { url breakpoint } }
        features { maxGuests maxExtraGuests { ageCategory number } }
      }
    }
  }
`;

const BOOKING_OPTIONS_GQL_DATA = gql`
  query InterhomeBooking ($articleId: ID!, $period: DatePeriod!, $participants: [TaxableAgeCategoryInput!]!, $data: JSON) {
    check: externalBookingCheck(
      item: { rentalAccommodation: { articleId: $articleId, period: $period } }
      participants: $participants
      partnerData: $data)
  }
`;

const transDefs = defineMessages({
  guests: { id: "interhome-guest-title", defaultMessage: "Personnes participant au séjour" },
  adult: { id: "interhome-guest-adult", defaultMessage: "à partir de 18 ans" },
  child: { id: "interhome-guest-child", defaultMessage: "de 2 à 17 ans" },
  baby: { id: "interhome-guest-baby", defaultMessage: "jusqu'à 24 mois" },
  maxGuests: { id: "interhome-max-guests", defaultMessage: "Max. {max, plural, =1 {une personne peut} other {# personnes peuvent}} séjourner dans cette location." },
  prices: { id: "interhome-price-title", defaultMessage: "Prix du séjour" },
  rent: { id: "interhome-price-rent", defaultMessage: "Loyer" },
  occupation: { id: "interhome-price-occupation", defaultMessage: "Avec une occupation {count, plural, =1 {d'une personne} other {de # personnes}}"},
  included: { id: "interhome-price-included-title", defaultMessage: "Inclus dans le prix" },
  options: { id: "interhome-options-title", defaultMessage: "{count, plural, =1 {Prestation supplémentaire} other {Prestations supplémentaires}} (en option)" },
  after: { id: "interhome-after-title", defaultMessage: "Frais supplémentaires obligatoires" },
  persons: { id: "interhome-persons-count", defaultMessage: "Pour {count, plural, =1 {une personne} other {# personnes}}" },
  back: { id: "interhome-error-back-article", defaultMessage: "Retour à l'article" }
});

type StaticInterhomeService = { label: string, info: string, unit?: AnyPrice };
type OptionedInterhomeService = { code: string, amount?: AnyPrice, multi: boolean, max: number, selected: number };
type InterhomeResume = {
  participantCount: number;
  includes: StaticInterhomeService[];
  options: (StaticInterhomeService & OptionedInterhomeService)[];
  supplements: StaticInterhomeService[];
  price: {
    bare: AnyPrice;
    optioned: AnyPrice;
    total: AnyPrice;
  }
  paymentModes: InterhomePayment[]
}

const InterhomeOptionPage = () => {

  const intl = useIntl();
  const client = useApolloClient();
  const navigate = useLocaleNavigate();
  const { print } = useLocaleFormatter();

  const [ ctx, dispatch ] = useInterhomeContext();
  const debouncedParticipants = useDebounce(ctx.participants, 500);

  const [ firstPriceLoading, setFirstPriceLoading ] = useState(true);
  const [ priceError, setPriceError ] = useState<InterhomeQueryErrorType | undefined>(undefined);
  const [ detailed, setDetailed ] = useState<InterhomeResume | undefined>(undefined);

  const { data, loading: mainLoading } = useQuery(RESUME_GQL_DATA, {
    ssr: false,
    variables: {
      articleId: ctx.item.articleId,
      period: ctx.item.period
    }
  });

  const checkPrice = useCallback(async (participants: TaxableAgeCategoryInput[], options?: any[]) => {
    const variables = {
      articleId: ctx.item.articleId,
      period: ctx.item.period,
      participants,
      data: { options: options || [] }
    };
    return client.query({  query: BOOKING_OPTIONS_GQL_DATA, fetchPolicy: "network-only", variables })
                 .then(({ data: { check } }) => check)
                 .catch(error => {
                   if (error.graphQLErrors.some((e: any) => e.extensions?.code === "EXTERNAL_BOOKING_UNAVAILABLE"))
                     throw new Error("unavailable");
                   throw new Error("unknown");
                 });
  }, [ ctx.item, client ]);

  const participantsConfig = useMemo(() => {
    const extraBabies = data?.article.features.maxExtraGuests?.find((eg: TaxableAgeCategoryInput) => eg.ageCategory === AgeCategory.BABY);
    return [
      { type: AgeCategory.ADULT, min: 1, helper: transDefs.adult },
      { type: AgeCategory.CHILD, helper: transDefs.child },
      { type: AgeCategory.BABY, ...!!extraBabies && { max: extraBabies.number, asExtra: true }, helper: transDefs.baby }
    ];
  }, [ data ]);

  const parsingInterhomeCheck = useCallback((data: any) => {
    const { price: { currency, ...prices }, services: { service } } = data;
    const castPrice = (amount: number) => ({ amount, currency });
    return {
      participantCount: debouncedParticipants.reduce((acc: number, { number }) => acc + number, 0),
      includes: service
        .filter(({ type }) => [ "Y2" ].includes(type))
        .map((s: any) => ({
          label: s.description,
          info: s.paymentInfo
        })),
      options: Object.values(
        service
          .filter(({ type }) => [ "N1", "N2", "N4" ].includes(type))
          .sort((a: any, b: any) => {
            const amountDiff = b.amount - a.amount;
            return amountDiff !== 0 ? amountDiff : a.code.localeCompare(b.code);
          })
          .reduce((acc: any[], opt: any) => {
            const multi = opt.count > 1;
            const selected = ctx.options?.find(co => co.code === opt.code)?.total || 0;
            acc[opt.code] = {
              code: opt.code,
              multi,
              ...(opt.type === "N1") && { amount: { amount: (multi && selected > 1 ? selected : 1) * opt.amount, currency } },
              max: Math.max(1, opt.count),
              selected,
              label: opt.description,
              info: opt.paymentInfo
            }
            return acc;
          }, {})),
      supplements: service
        .filter(({ type }) => [ "Y4", "Y5", "Y6" ].includes(type))
        .sort((a: any, b: any) => {
          const amountDiff = b.amount - a.amount;
          return amountDiff !== 0 ? amountDiff : a.code.localeCompare(b.code);
        })
        .map((s: any) => ({
          label: s.description,
          info: s.paymentInfo,
          ...(s.amount > 0) && { unit: castPrice(s.amount) }
        })),
      price: {
        bare: castPrice(prices.rentalPrice),
        optioned: castPrice(prices.customerPrice),
        total: castPrice(prices.customerTotalPrice)
      },
      paymentModes: [
        { type: InterhomePaymentMode.CC, amount: castPrice(prices.customerTotalPrice) },
        ...(() => {
          if (!prices.amountPrepayment) return [];
          return [
            {
              type: InterhomePaymentMode.CC,
              amount: castPrice(prices.amountPrepayment),
              balance: castPrice(prices.customerTotalPrice - prices.amountPrepayment)
            }
          ]
        })(),
        ...(() => {
          if (castPeriod(ctx.item.period).start.isSameOrBefore(moment().add(14, "day"), "day")) return [];
          return [
            {
              type: InterhomePaymentMode.Invoice,
              amount: castPrice(prices.amountPrepayment || prices.customerTotalPrice),
              dueDate: prices.dueDatePrepayment || prices.dueDateFinalpayment,
              ...(!!prices.amountPrepayment) && {
                balance: castPrice(prices.customerTotalPrice - prices.amountPrepayment),
                balanceDueDate: prices.dueDateFinalpayment
              }
            }
          ];
        })()
      ]
    } as InterhomeResume;
  }, [ debouncedParticipants, ctx.options ]);

  useEffect(() => dispatch({ type: "change.index", payload: 1 }), []);

  useEffect(() => {
    const handlePrice = async () => {
      try {
        const result = await checkPrice(debouncedParticipants, ctx.options);
        setDetailed(parsingInterhomeCheck(result));
        !!priceError && setPriceError(undefined);
      } catch(error) {
        setPriceError((error as { message: InterhomeQueryErrorType }).message);
      } finally {
        firstPriceLoading && setFirstPriceLoading(false);
      }
    }
    handlePrice().then();
  }, [ debouncedParticipants, ctx.options, checkPrice ]);

  useEffect(() => {
    if (!detailed) return;
    const payload = { total: detailed.price.total, paymentModes: detailed.paymentModes };
    dispatch({ type: "change.resume", payload });
  }, [ detailed ]);


  const onParticipantsChange = (payload: TaxableAgeCategoryInput[]) => {
    dispatch({ type: "change.participants", payload });
  }

  const onOneOptionChange = (code: string, value: number | boolean) => {
    const total = typeof value === "boolean" ? Number(value) : value;
    dispatch({ type: "change.options", payload: { code, total } });
  }

  const onNext = () => {
    const params = interhomeUrlParams(ctx.item.articleId, ctx.item.period);
    navigate(`external/cart/interhome/information?${params}`);
  }

  const onBack = () => navigate(buildArticleUrl(data.article!));

  if (mainLoading || !data || firstPriceLoading)
    return (<Skeleton className="cart-skeleton" />);

  return (
    <section className="cart-content interhome">

      <CartItem className="cart-item-wrapper"
                data={{
                  title: data.article.title.value,
                  description: data.article.address.resume,
                  image: data.article.images[0]?.assets.find((a: Asset) => a.breakpoint === "MD")?.url,
                  articleId: ctx.item.articleId,
                  period: ctx.item.period
                } as any}
                withPrice={false}
                nextStep={(
                  <div className="cart-price-detail">
                    <h2>{intl.formatMessage(transDefs.guests)}</h2>
                    <ParticipantsInput categories={participantsConfig}
                                       value={ctx.participants}
                                       onSelection={onParticipantsChange}
                                       max={data?.article.features.maxGuests || 1} />
                    <InfoBox className="guests-info" minimal={true} inline={false}
                             msg={intl.formatMessage(transDefs.maxGuests, { max: data.article?.features.maxGuests || 1 })} />
                  </div>)} />

      {!!detailed &&
        <>
          <div className="cart-item-wrapper interhome-price r-form">

            <h2>{intl.formatMessage(transDefs.prices)}</h2>
            <div className="p-item">
              <div>
                <span className="name">{intl.formatMessage(transDefs.rent)}</span>
                <span className="price">{print.price(detailed.price.bare)}</span>
              </div>
              <span className="info">
                {intl.formatMessage(transDefs.occupation, { count: detailed.participantCount })}</span>
            </div>

            {detailed.includes.length > 0 &&
              <>
                <h3>{intl.formatMessage(transDefs.included)}</h3>
                {detailed.includes.map((item, index) => (
                  <div key={index} className="p-item">
                    <div>{item.label}</div>
                    <span className="info">{item.info}</span>
                  </div>)
                )}
              </>
            }

            {detailed.options.length > 0 &&
              <>
                <h3>{intl.formatMessage(transDefs.options, { count: detailed.options.length })}</h3>
                {detailed.options.map(item => (
                  <div key={item.code} className="p-item">
                    <div>

                      {item.multi &&
                        <select id={item.code}
                                defaultValue={item.selected}
                                data-unit={item.selected}
                                onChange={e => onOneOptionChange(item.code, +e.target.value)}>
                          {Array.from({ length: item.max + 1 }).map((_, index) =>
                            <option key={index} value={index}>{index === 0 ? "-" : index}</option>
                          )}
                        </select>
                      }

                      {!item.multi &&
                        <label className="rf-checkbox">
                          <input type="checkbox"
                                 id={item.code}
                                 defaultChecked={item.selected === 1}
                                 onChange={e => onOneOptionChange(item.code, e.target.checked)} />
                          <span className="rf-mark" />
                        </label>
                      }

                      <label htmlFor={item.code} className="name">{item.label}</label>
                      {!!item.amount &&
                        <span className="price">{print.price(item.amount)}</span>
                      }
                    </div>
                    <div className="info">{(!!item.unit) ? `${print.price(item.unit)} - ${item.info}` : item.info}</div>
                  </div>
                ))}
              </>
            }

            <div className="p-item tot">
              <div>
                <span className="name">{intl.formatMessage(bookingTranslations.total)}</span>
                <span className="price">{print.price(detailed.price.optioned)}</span>
              </div>
              <span className="info">
                {intl.formatMessage(transDefs.persons, { count: detailed.participantCount })}
              </span>
            </div>
          </div>

          {(detailed.supplements.length > 0) &&
            <div className="cart-item-wrapper interhome-price">
              <h2>{intl.formatMessage(transDefs.after)}</h2>
              {detailed.supplements.map((item, index) => (
                <div key={index} className="p-item">
                  <div>{item.label}</div>
                  <div className="info">{(!!item.unit) ? `${print.price(item.unit)} - ${item.info}` : item.info}</div>
                </div>)
              )}
            </div>
          }

          <div className="cart-full-price rl-two-col stepper-actions">
            <div />
            <div className="cart-item-wrapper margin">
              <CartPrice data={{ amount: detailed.price.total }} />
              <button type="button"
                      className="step-btn"
                      onClick={onNext}>
                {intl.formatMessage(bookingTranslations.book)}
              </button>
            </div>
          </div>

        </>
      }

      {!!priceError &&
        <div className="cart-item-wrapper">
          <InterhomeErrorDisplay type={priceError}
                                 actionLabel={intl.formatMessage(transDefs.back)}
                                 withIcon={true}
                                 onAction={onBack} />
        </div>
      }

    </section>);

}

export default InterhomeOptionPage;
